import { zodResolver } from '@hookform/resolvers/zod';
import {
  allocationSchema,
  createAllocationsSchema,
  deployTokenSchema,
  nodeConfigurationSchema,
  nodeSchema,
  okSchema,
  paginated,
  type NodeSummary,
} from '@hearth/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft,
  Copy,
  KeyRound,
  Loader2,
  Plus,
  RefreshCw,
  Trash2,
  ServerCog as ServerCogIcon,
} from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { Confirm } from '@/components/confirm';
import { FormField } from '@/components/form-field';
import { LazyCodeEditor } from '@/components/lazy-code-editor';
import { NodeForm } from '@/components/node-form';
import { PageHeader } from '@/components/page-header';
import { Pagination } from '@/components/pagination';
import { QueryState } from '@/components/query-state';
import { NodeHealthBadge } from '@/components/status-badges';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { UsageBar } from '@/components/usage-bar';
import { apiRequest } from '@/lib/api';
import { nodeQuery } from '@/lib/admin-queries';
import { applyServerErrors, errorMessage } from '@/lib/forms';
import { timeAgo } from '@/lib/format';

const TABS = ['overview', 'settings', 'configuration', 'allocations'] as const;
type Tab = (typeof TABS)[number];

export const Route = createFileRoute('/_app/admin/nodes/$nodeId')({
  validateSearch: (s: Record<string, unknown>): { tab?: Tab } => ({
    tab: TABS.includes(s.tab as Tab) ? (s.tab as Tab) : undefined,
  }),
  component: NodePage,
});

function NodePage() {
  const nodeId = Number(Route.useParams().nodeId);
  const { tab = 'overview' } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const query = useQuery(nodeQuery(nodeId));
  return (
    <>
      <Button asChild variant="ghost" size="sm" className="mb-4 -ml-2">
        <Link to="/admin/nodes">
          <ArrowLeft /> Nodes
        </Link>
      </Button>
      <QueryState query={query}>
        {(node) => (
          <>
            <PageHeader
              title={node.name}
              icon={ServerCogIcon}
              description={
                <span className="flex items-center gap-2">
                  <NodeHealthBadge health={node.health} />
                  <span className="font-mono text-xs">
                    {node.scheme}://{node.fqdn}:{node.daemonPort}
                  </span>
                </span>
              }
            />
            <Tabs value={tab} onValueChange={(v) => void navigate({ search: { tab: v as Tab } })}>
              <TabsList>
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="settings">Settings</TabsTrigger>
                <TabsTrigger value="configuration">Configuration</TabsTrigger>
                <TabsTrigger value="allocations">Allocations ({node.allocationCount})</TabsTrigger>
              </TabsList>
              <TabsContent value="overview" className="mt-6">
                <Overview node={node} />
              </TabsContent>
              <TabsContent value="settings" className="mt-6">
                <Settings node={node} />
              </TabsContent>
              <TabsContent value="configuration" className="mt-6">
                <Configuration node={node} />
              </TabsContent>
              <TabsContent value="allocations" className="mt-6">
                <Allocations node={node} />
              </TabsContent>
            </Tabs>
          </>
        )}
      </QueryState>
    </>
  );
}

function Overview({ node }: { node: NodeSummary }) {
  const queryClient = useQueryClient();
  const refresh = useMutation({
    mutationFn: () =>
      apiRequest(`/api/application/nodes/${node.id}/health`, {
        method: 'POST',
        schema: nodeSchema,
      }),
    onSuccess: (n) => queryClient.setQueryData(['admin', 'nodes', node.id], n),
    onError: (e) => toast.error(errorMessage(e)),
  });
  const sys = node.health.system;
  const gib = (b?: number) => (b ? `${(b / 1024 ** 3).toFixed(1)} GiB` : '—');
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between">
          <div className="grid gap-1.5">
            <CardTitle>Daemon</CardTitle>
            <CardDescription>
              {node.health.lastSeenAt
                ? `Last heard from ${timeAgo(node.health.lastSeenAt)}`
                : 'hearthd has never answered.'}
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refresh.mutate()}
            disabled={refresh.isPending}
          >
            <RefreshCw className={refresh.isPending ? 'animate-spin' : undefined} /> Check now
          </Button>
        </CardHeader>
        <CardContent className="grid gap-3 text-sm">
          {node.health.error && (
            <Alert variant="destructive">
              <AlertTitle>Problem</AlertTitle>
              <AlertDescription>{node.health.error}</AlertDescription>
            </Alert>
          )}
          {sys ? (
            <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2">
              <dt className="text-muted-foreground">hearthd</dt>
              <dd>
                {sys.version} on {sys.os}/{sys.arch} ({sys.cpuCount} CPUs)
              </dd>
              <dt className="text-muted-foreground">Container runtime</dt>
              <dd>
                {sys.docker.runtime} {sys.docker.serverVersion} ({sys.docker.arch},{' '}
                {sys.docker.cpus} CPUs, {gib(sys.docker.memoryBytes)})
              </dd>
              <dt className="text-muted-foreground">amd64 emulation</dt>
              <dd>
                {sys.docker.amd64Emulation === undefined
                  ? 'not probed yet'
                  : sys.docker.amd64Emulation
                    ? 'available'
                    : 'not available'}
              </dd>
              <dt className="text-muted-foreground">I/O weight limits</dt>
              <dd>
                {sys.docker.ioWeight === undefined
                  ? 'not probed yet'
                  : sys.docker.ioWeight
                    ? 'supported'
                    : 'not supported (ignored)'}
              </dd>
            </dl>
          ) : (
            <p className="text-muted-foreground">
              Configure and start hearthd on this machine; its details will show here.
            </p>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Allocated capacity</CardTitle>
          <CardDescription>
            {node.serverCount} server{node.serverCount === 1 ? '' : 's'} · overallocation{' '}
            {node.memoryOverallocate}% memory, {node.diskOverallocate}% disk
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          <UsageBar label="Memory" used={node.allocated.memoryMb} total={node.memoryMb} />
          <UsageBar label="Disk" used={node.allocated.diskMb} total={node.diskMb} />
        </CardContent>
      </Card>
    </div>
  );
}

function Settings({ node }: { node: NodeSummary }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const save = useMutation({
    mutationFn: (body: unknown) =>
      apiRequest(`/api/application/nodes/${node.id}`, {
        method: 'PATCH',
        body,
        schema: nodeSchema,
      }),
  });
  const remove = useMutation({
    mutationFn: () =>
      apiRequest(`/api/application/nodes/${node.id}`, { method: 'DELETE', schema: okSchema }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'nodes'] });
      await navigate({ to: '/admin/nodes' });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <div className="grid gap-6">
      <NodeForm
        submitLabel="Save changes"
        pending={save.isPending}
        initial={{ ...node, locationId: node.location.id, description: node.description ?? '' }}
        onSubmit={(values, setError) =>
          save.mutate(values, {
            onSuccess: (n) => {
              queryClient.setQueryData(['admin', 'nodes', node.id], n);
              toast.success(
                'Node saved. Regenerate hearthd’s config if you changed ports or the address.',
              );
            },
            onError: (e) => applyServerErrors(e, setError),
          })
        }
      />
      <Card className="border-destructive/40">
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="grid gap-1.5">
            <CardTitle>Delete node</CardTitle>
            <CardDescription>
              Only possible once it has no servers. Its allocations are deleted too.
            </CardDescription>
          </div>
          <Confirm
            title={`Delete ${node.name}?`}
            description="This can't be undone."
            action="Delete node"
            destructive
            onConfirm={() => remove.mutate()}
          >
            <Button variant="destructive" disabled={node.serverCount > 0}>
              <Trash2 /> Delete
            </Button>
          </Confirm>
        </CardHeader>
      </Card>
    </div>
  );
}

function Configuration({ node }: { node: NodeSummary }) {
  const queryClient = useQueryClient();
  const config = useQuery({
    queryKey: ['admin', 'nodes', node.id, 'configuration', node.tokenId],
    queryFn: ({ signal }) =>
      apiRequest(`/api/application/nodes/${node.id}/configuration`, {
        schema: nodeConfigurationSchema,
        signal,
      }),
    staleTime: Infinity,
  });
  const [deploy, setDeploy] = useState<z.infer<typeof deployTokenSchema> | null>(null);
  const issue = useMutation({
    mutationFn: () =>
      apiRequest(`/api/application/nodes/${node.id}/deploy-token`, {
        method: 'POST',
        schema: deployTokenSchema,
      }),
    onSuccess: setDeploy,
    onError: (e) => toast.error(errorMessage(e)),
  });
  const reset = useMutation({
    mutationFn: () =>
      apiRequest(`/api/application/nodes/${node.id}/reset-token`, {
        method: 'POST',
        schema: nodeSchema,
      }),
    onSuccess: async (n) => {
      queryClient.setQueryData(['admin', 'nodes', node.id], n);
      toast.success('Token rotated. Update hearthd’s config.yml or it will be locked out.');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const copy = (text: string) =>
    navigator.clipboard.writeText(text).then(
      () => toast.success('Copied.'),
      (e: unknown) => toast.error(errorMessage(e)),
    );

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
      <Card>
        <CardHeader>
          <CardTitle>config.yml</CardTitle>
          <CardDescription>
            hearthd reads this from <code>~/Library/Application Support/Hearth/config.yml</code> by
            default. It contains the node’s secret token.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <QueryState query={config}>
            {(c) => (
              <LazyCodeEditor
                value={c.yaml}
                language="yaml"
                readOnly
                height={420}
                ariaLabel="hearthd configuration"
              />
            )}
          </QueryState>
        </CardContent>
      </Card>
      <div className="grid content-start gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Auto-deploy</CardTitle>
            <CardDescription>
              Generate a one-time command that writes this config on the node. It expires in 15
              minutes.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3">
            {deploy ? (
              <>
                <code className="bg-muted block rounded-md p-3 font-mono text-xs break-all">
                  {deploy.command}
                </code>
                <Button variant="outline" onClick={() => void copy(deploy.command)}>
                  <Copy /> Copy command
                </Button>
              </>
            ) : (
              <Button onClick={() => issue.mutate()} disabled={issue.isPending}>
                {issue.isPending && <Loader2 className="animate-spin" />} Generate token
              </Button>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Node token</CardTitle>
            <CardDescription>
              ID <code>{node.tokenId}</code>. Rotate it if the config file leaked.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Confirm
              title="Rotate the node token?"
              description="hearthd stops working until its config.yml has the new token."
              action="Rotate"
              destructive
              onConfirm={() => reset.mutate()}
            >
              <Button variant="outline">
                <KeyRound /> Rotate token
              </Button>
            </Confirm>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Allocations({ node }: { node: NodeSummary }) {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<'all' | 'free' | 'assigned'>('all');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [adding, setAdding] = useState(false);
  const query = useQuery({
    queryKey: ['admin', 'nodes', node.id, 'allocations', page, filter],
    queryFn: ({ signal }) =>
      apiRequest(
        `/api/application/nodes/${node.id}/allocations?page=${page}&perPage=50&filter=${filter}`,
        { schema: paginated(allocationSchema), signal },
      ),
    placeholderData: keepPreviousData,
  });
  const remove = useMutation({
    mutationFn: (ids: number[]) =>
      apiRequest(`/api/application/nodes/${node.id}/allocations/delete`, {
        method: 'POST',
        body: { ids },
        schema: z.object({ deleted: z.number() }),
      }),
    onSuccess: async (r) => {
      setSelected(new Set());
      await queryClient.invalidateQueries({ queryKey: ['admin', 'nodes'] });
      toast.success(`Deleted ${r.deleted} allocation(s).`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const toggle = (id: number) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Tabs
          value={filter}
          onValueChange={(v) => {
            setFilter(v as typeof filter);
            setPage(1);
          }}
        >
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="free">Free</TabsTrigger>
            <TabsTrigger value="assigned">Assigned</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="ml-auto flex gap-2">
          {selected.size > 0 && (
            <Confirm
              title={`Delete ${selected.size} allocation(s)?`}
              description="Only unassigned allocations can be deleted."
              action="Delete"
              destructive
              onConfirm={() => remove.mutate([...selected])}
            >
              <Button variant="outline">
                <Trash2 /> Delete {selected.size}
              </Button>
            </Confirm>
          )}
          <Button onClick={() => setAdding(true)}>
            <Plus /> Add ports
          </Button>
        </div>
      </div>
      <QueryState query={query}>
        {(data) => (
          <>
            <div className="table-card">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10" />
                    <TableHead>Address</TableHead>
                    <TableHead>Alias</TableHead>
                    <TableHead>Server</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.data.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-muted-foreground py-8 text-center">
                        No allocations. Add the ports game servers on this node may use.
                      </TableCell>
                    </TableRow>
                  )}
                  {data.data.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell>
                        <Checkbox
                          checked={selected.has(a.id)}
                          disabled={!!a.server}
                          onCheckedChange={() => toggle(a.id)}
                          aria-label={`Select ${a.ip}:${a.port}`}
                        />
                      </TableCell>
                      <TableCell className="font-mono">
                        {a.ip}:{a.port}
                      </TableCell>
                      <TableCell className="text-muted-foreground">{a.ipAlias ?? '—'}</TableCell>
                      <TableCell>
                        {a.server ? (
                          <Link
                            to="/admin/servers/$serverId"
                            params={{ serverId: String(a.server.id) }}
                            className="hover:underline"
                          >
                            {a.server.name}
                          </Link>
                        ) : (
                          <Badge variant="outline">Free</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-xs">
                        {a.notes ?? ''}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <Pagination {...data.meta} onPageChange={setPage} />
          </>
        )}
      </QueryState>
      {adding && <AddAllocations nodeId={node.id} onClose={() => setAdding(false)} />}
    </div>
  );
}

type AllocValues = z.input<typeof createAllocationsSchema>;

function AddAllocations({ nodeId, onClose }: { nodeId: number; onClose: () => void }) {
  const queryClient = useQueryClient();
  const form = useForm<AllocValues>({
    resolver: zodResolver(createAllocationsSchema),
    defaultValues: { ip: '0.0.0.0', ports: '25565-25575' },
  });
  const add = useMutation({
    mutationFn: (body: AllocValues) =>
      apiRequest(`/api/application/nodes/${nodeId}/allocations`, {
        method: 'POST',
        body: { ...body, ipAlias: body.ipAlias || null },
        schema: z.object({ created: z.number(), skipped: z.number() }),
      }),
    onSuccess: async (r) => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'nodes'] });
      toast.success(
        `Added ${r.created} port(s)${r.skipped ? `; ${r.skipped} already existed` : ''}.`,
      );
      onClose();
    },
    onError: (e) => applyServerErrors(e, form.setError),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add allocations</DialogTitle>
          <DialogDescription>
            Ports game servers can bind (TCP and UDP). Open them in your router and firewall to
            reach them from outside.
          </DialogDescription>
        </DialogHeader>
        <form
          id="alloc-form"
          className="grid gap-4"
          noValidate
          onSubmit={form.handleSubmit((v) => add.mutate(v))}
        >
          <FormField
            label="IP address"
            error={form.formState.errors.ip?.message}
            description="0.0.0.0 listens on every interface."
          >
            <Input {...form.register('ip')} />
          </FormField>
          <FormField
            label="Alias (optional)"
            error={form.formState.errors.ipAlias?.message}
            description="Shown to users instead of the IP, e.g. play.example.com."
          >
            <Input {...form.register('ipAlias')} />
          </FormField>
          <FormField
            label="Ports"
            error={form.formState.errors.ports?.message}
            description="Single ports or ranges, comma separated. Up to 1000."
          >
            <Input {...form.register('ports')} />
          </FormField>
        </form>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="alloc-form" disabled={add.isPending}>
            {add.isPending && <Loader2 className="animate-spin" />} Add
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
